import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
    Box, Stack, Typography, IconButton, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'sonner';
import { MuiButton } from '@ace/ace-ui-web';
import { Calendar, momentLocalizer, View } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../calendar-view-report/calendarReport.css';
import { fetchCalendarReport } from "../../redux/CalendarReportReducer/calendarReportSlice";
import { fetchGigRequests, updateGigShift, cancelGigShift } from "../../redux/GigRequestsReducer/gigRequestsSlice";
import { Teams, fetchSkills as fetchTeams } from "../../redux/TeamReducer/teamSlice";
import { Skills, fetchSkills } from "../../redux/SkillReducer/skillSlice";
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { transformCalendarData, transformGigRequestDetails } from '../../utils/calendarReportTransform';
import CalendarEventComponent from '../../components/calendar/CalendarEventComponent';
import CalendarCustomToolbar from '../../components/calendar/CalendarCustomToolbar';
import ShiftDetailCard from '../../components/calendar/ShiftDetailCard';
import { AggregatedEvent } from '../../types/calendarTypes';
import { GigShiftUpdateDetail } from '../../types/gigApi';
import { getStatusColor, statusLegend, aggregateEvents } from '../../utils/calendarUtils';

const localizer = momentLocalizer(moment);

const getNavigationUnit = (view: View): moment.unitOfTime.DurationConstructor => {
    if (view === 'month') return 'months';
    if (view === 'week') return 'weeks';
    return 'days';
};

/**
 * My Shifts calendar view for GIG workers.
 * Shows only the current user's shifts (as poster on gig shifts, or as claimant
 * on regular shifts), styled with the same status-based colors as the manager
 * calendar report. Defaults to the week view.
 *
 * In the day view, the shift detail (the same "Summary" card normally shown
 * in the click-to-open dialog) is rendered directly beneath the calendar for
 * every shift on the selected day — no click required.
 */
const GigWorkerShiftsCalendar: React.FC = () => {
    const dispatch = useAppDispatch();
    const { data, loading, error } = useAppSelector((state) => state.calendarReport);
    const currentUser = useAppSelector((state) => state.currentUser.user);
    const gigShifts = useAppSelector((state) => state.gigRequests.details);
    const { teams } = useAppSelector((state) => state.teams);
    const { skills } = useAppSelector((state) => state.skills);

    // Map skill code -> long-form description so gig events (which only carry
    // the code in skillDesc) can still be shown with the readable label.
    const skillLabelByCd = useMemo(() => {
        const map: Record<string, string> = {};
        (skills || []).forEach((s: Skills) => {
            map[s.lookupValueCd] = s.lookupValueDesc;
        });
        return map;
    }, [skills]);

    // Shift options mirror the create gig request form (shifts 1, 2, 3).
    const shiftOptions = useMemo(() => (
        Array.isArray(teams)
            ? teams
                .filter((t: Teams) => ['1', '2', '3'].includes(t.lookupValueCd))
                .map((t: Teams) => ({ label: t.lookupValueDesc, value: t.lookupValueCd }))
            : []
    ), [teams]);

    const [currentDate, setCurrentDate] = useState<Date>(new Date());
    const [view, setView] = useState<View>('week');
    const [selectedEvent, setSelectedEvent] = useState<AggregatedEvent | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [draftRows, setDraftRows] = useState<Array<{ requestSeqId: number; date: string; hours: string }>>([]);
    const [draftShiftCd, setDraftShiftCd] = useState<string>('');
    const [draftNotes, setDraftNotes] = useState<string>('');
    const [draftStartTime, setDraftStartTime] = useState<string>('');
    const [draftEndTime, setDraftEndTime] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const [cancelTarget, setCancelTarget] = useState<AggregatedEvent | null>(null);
    const [isCancelling, setIsCancelling] = useState(false);

    const rawEvents = useMemo(() => {
        const regular = data ? transformCalendarData(data) : [];
        const gig = gigShifts ? transformGigRequestDetails(gigShifts) : [];
        return [...regular, ...gig];
    }, [data, gigShifts]);

    // Restrict to shifts belonging to the current user.
    // - Gig shifts: current user is the poster (postedByUserId).
    // - Regular shifts: current user is the claimant (claimedBy).
    const myEvents = useMemo(() => {
        const workdayId = currentUser?.workdayId;
        const userId = currentUser?.userId;
        if (!workdayId && !userId) return [];
        return rawEvents.filter((e) => {
            if (e.shiftType === 'GIG') {
                return !!e.postedByUserId && (e.postedByUserId === workdayId || e.postedByUserId === userId);
            }
            return !!e.claimedBy && (e.claimedBy === workdayId || e.claimedBy === userId);
        });
    }, [rawEvents, currentUser?.workdayId, currentUser?.userId]);

    const aggregatedEvents = useMemo(() => aggregateEvents(myEvents), [myEvents]);

    // Shifts on the currently selected day (used to render the inline detail
    // cards directly, without needing a click, when view === 'day').
    const dayEvents = useMemo(() => {
        if (view !== 'day') return [];
        const dayKey = moment(currentDate).format('YYYY-MM-DD');
        return aggregatedEvents.filter((e) => moment(e.start).format('YYYY-MM-DD') === dayKey);
    }, [view, currentDate, aggregatedEvents]);

    useEffect(() => {
        if (!currentUser?.siteCd) return;
        // Pad to full calendar weeks (month view shows leading/trailing days
        // from adjacent months) plus an extra week buffer on each side as a
        // safety margin against timezone/boundary edge cases.
        const startDate = moment(currentDate)
            .startOf('month')
            .startOf('week')
            .subtract(1, 'week')
            .format('YYYY-MM-DD');
        const endDate = moment(currentDate)
            .endOf('month')
            .endOf('week')
            .add(1, 'week')
            .format('YYYY-MM-DD');
        dispatch(fetchCalendarReport({ siteCd: currentUser.siteCd, startDate, endDate }));
        // Scope gig requests to the current user so we only get their gig shifts.
        const userId = currentUser.workdayId || currentUser.userId;
        dispatch(fetchGigRequests({ siteCd: currentUser.siteCd, userId, statusFilter: 'ALL' }));
    }, [dispatch, currentDate, currentUser?.siteCd, currentUser?.workdayId, currentUser?.userId]);

    useEffect(() => {
        dispatch(fetchTeams());
    }, [dispatch]);

    useEffect(() => {
        if (currentUser?.siteCd) dispatch(fetchSkills(currentUser.siteCd));
    }, [dispatch, currentUser?.siteCd]);

    const handleNavigate = useCallback((newDate: Date) => setCurrentDate(newDate), []);
    const handleViewChange = useCallback((newView: View) => setView(newView), []);

    // Toolbar is now rendered independently of the Calendar component (so it
    // stays visible even when the day view swaps the calendar out for the
    // detail cards), so we compute the new date from the action ourselves
    // instead of relying on react-big-calendar's internal toolbar wiring.
    const handleToolbarNavigate = useCallback((action: 'PREV' | 'NEXT' | 'TODAY') => {
        setCurrentDate((prev) => {
            if (action === 'TODAY') return new Date();
            const amount = action === 'PREV' ? -1 : 1;
            const unit = getNavigationUnit(view);
            return moment(prev).add(amount, unit).toDate();
        });
    }, [view]);
    const handleSelectEvent = useCallback((event: AggregatedEvent) => {
        setSelectedEvent(event);
        setIsEditing(false);
        setIsModalOpen(true);
    }, []);
    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedEvent(null);
        setIsEditing(false);
    }, []);

    // A gig event is editable while it's still Pending or Posted.
    const isGigEvent = selectedEvent?.resource.shiftType === 'GIG';
    const editableStatus = selectedEvent?.resource.status === 'PENDING APPROVAL'
        || selectedEvent?.resource.status === 'POSTED';
    const canEdit = !!isGigEvent && !!editableStatus;

    // All rows in an aggregated event share the same requestId (they're grouped
    // by shift-type/status/team/skill/day). Use the first row to derive it.
    const selectedRequestId = selectedEvent?.resource.events[0]?.requestId;

    const handleStartEdit = useCallback((eventOverride?: AggregatedEvent) => {
        const target = eventOverride ?? selectedEvent;
        if (!target) return;
        setSelectedEvent(target);
        setDraftRows(
            target.resource.events
                .filter((e) => typeof e.requestSeqId === 'number')
                .map((e) => ({
                    requestSeqId: e.requestSeqId as number,
                    date: moment(e.date).format('YYYY-MM-DD'),
                    hours: e.requestHours != null ? String(e.requestHours) : '',
                }))
        );
        setDraftShiftCd(target.resource.events[0]?.team ?? '');
        setDraftNotes(target.resource.events[0]?.requestNotes ?? '');
        setDraftStartTime(target.resource.events[0]?.startTime ?? '');
        setDraftEndTime(target.resource.events[0]?.endTime ?? '');
        setIsEditing(true);
    }, [selectedEvent]);

    const handleCancelEdit = useCallback(() => {
        setIsEditing(false);
    }, []);

    const updateDraftRow = (index: number, patch: Partial<{ date: string; hours: string }>) => {
        setDraftRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    };

    // True when a given aggregated event is the one currently being edited
    // (used to decide which inline day-view card shows the edit form).
    const isEditingEvent = (event: AggregatedEvent) => isEditing && selectedEvent?.id === event.id;

    const handleSaveEdit = useCallback(async () => {
        if (!currentUser?.siteCd || !selectedRequestId || !currentUser?.workdayId) {
            toast.error('Missing user or request information.');
            return;
        }

        const details: GigShiftUpdateDetail[] = draftRows.map((r) => ({
            requestSeqId: r.requestSeqId,
            requestDt: r.date || null,
            startTime: draftStartTime || null,
            endTime: draftEndTime || null,
            requestHours: r.hours === '' ? null : parseFloat(r.hours),
        }));

        // Validate hours
        const badHours = details.find(
            (d) => d.requestHours !== null && (Number.isNaN(d.requestHours as number) || (d.requestHours as number) <= 0)
        );
        if (badHours) {
            toast.error('Hours must be a positive number.');
            return;
        }

        setIsSaving(true);
        try {
            await dispatch(
                updateGigShift({
                    siteCd: currentUser.siteCd,
                    requestId: selectedRequestId,
                    lastUpdateByUser: currentUser.workdayId,
                    requestedShiftCd: draftShiftCd || null,
                    requestNotes: draftNotes || null,
                    startTime: draftStartTime || null,
                    endTime: draftEndTime || null,
                    details,
                })
            ).unwrap();

            toast.success('Gig shift updated successfully!');
            setIsEditing(false);
            handleCloseModal();
            // Refresh gig data.
            const userId = currentUser.workdayId || currentUser.userId;
            dispatch(fetchGigRequests({ siteCd: currentUser.siteCd, userId, statusFilter: 'ALL' }));
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update gig shift.');
        } finally {
            setIsSaving(false);
        }
    }, [
        dispatch,
        currentUser?.siteCd,
        currentUser?.workdayId,
        currentUser?.userId,
        selectedRequestId,
        draftRows,
        draftShiftCd,
        draftNotes,
        draftStartTime,
        draftEndTime,
        handleCloseModal,
    ]);

    // A gig event can be cancelled by the requesting worker while it is still
    // Pending (not yet Approved/Denied/Cancelled).
    const isCancellableEvent = (event: AggregatedEvent) =>
        event.resource.shiftType === 'GIG' && event.resource.status === 'PENDING APPROVAL';

    const requestSeqIdsForEvent = (event: AggregatedEvent) =>
        event.resource.events
            .filter((e) => typeof e.requestSeqId === 'number')
            .map((e) => e.requestSeqId as number);

    const handleOpenCancel = useCallback((event: AggregatedEvent) => {
        setCancelTarget(event);
    }, []);

    const handleCloseCancel = useCallback(() => {
        setCancelTarget(null);
    }, []);

    const handleConfirmCancel = useCallback(async () => {
        if (!cancelTarget || !currentUser?.siteCd) {
            toast.error('Missing user or request information.');
            return;
        }
        const requestId = cancelTarget.resource.events[0]?.requestId;
        if (!requestId) {
            toast.error('Unable to determine the request to cancel.');
            return;
        }
        const requestSeqIds = requestSeqIdsForEvent(cancelTarget);
        if (requestSeqIds.length === 0) {
            toast.error('Unable to determine the request row(s) to cancel.');
            return;
        }

        setIsCancelling(true);
        try {
            await Promise.all(
                requestSeqIds.map((requestSeqId) =>
                    dispatch(
                        cancelGigShift({
                            site_cd: currentUser.siteCd as string,
                            request_id: requestId,
                            request_seq_id: requestSeqId,
                            cancelledByUserId: currentUser.workdayId || currentUser.userId || undefined,
                            cancelledByUserName: currentUser.firstName || currentUser.lastName
                                ? `${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`.trim()
                                : undefined,
                        })
                    ).unwrap()
                )
            );

            toast.success('Gig shift request cancelled.');
            setCancelTarget(null);
            handleCloseModal();
            const userId = currentUser.workdayId || currentUser.userId;
            dispatch(fetchGigRequests({ siteCd: currentUser.siteCd, userId, statusFilter: 'ALL' }));
        } catch (err: any) {
            toast.error(err?.message || 'Failed to cancel gig shift request.');
        } finally {
            setIsCancelling(false);
        }
    }, [dispatch, currentUser?.siteCd, currentUser?.workdayId, currentUser?.userId, currentUser?.firstName, currentUser?.lastName, cancelTarget, handleCloseModal]);

    const eventStyleGetter = (event: AggregatedEvent) => ({
        style: {
            backgroundColor: getStatusColor(event.resource.status),
            borderRadius: '4px',
            opacity: 0.95,
            color: 'black',
            border: 'none',
            display: 'block'
        }
    });

    const calendarComponents = useMemo(() => ({
        event: (props: any) => (
            <CalendarEventComponent {...props} skillLabelByCd={skillLabelByCd} />
        )
    }), [skillLabelByCd]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">Error loading calendar data: {error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', p: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    My Shifts
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    Browse and view shifts you have requested or claimed.
                </Typography>
            </Box>
            <CalendarCustomToolbar
                date={currentDate}
                view={view}
                onNavigate={handleToolbarNavigate}
                onView={handleViewChange}
            />
            <Box className={view === 'day' ? 'calendar-report-container calendar-report-container--day' : 'calendar-report-container'}>
                {view === 'day' ? (
                    /* Day view: render the shift detail card(s) in place of the
                       calendar and the click-to-open dialog, so no click is
                       required to see (or edit) the summary. */
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, height: '100%', overflowY: 'auto' }}>
                        {dayEvents.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                                <Typography variant="body2">No shifts on this day.</Typography>
                            </Box>
                        ) : (
                            dayEvents.map((event) => {
                                const editing = isEditingEvent(event);
                                const eventIsGig = event.resource.shiftType === 'GIG';
                                const eventEditableStatus = event.resource.status === 'PENDING APPROVAL'
                                    || event.resource.status === 'POSTED';
                                const eventCanEdit = eventIsGig && eventEditableStatus;

                                return (
                                    <Box key={event.id} sx={{ width: '100%', maxWidth: 640, border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                                        <ShiftDetailCard
                                            event={event}
                                            skillLabelByCd={skillLabelByCd}
                                            isEditing={editing}
                                            draftNotes={draftNotes}
                                            onDraftNotesChange={setDraftNotes}
                                            draftRows={draftRows}
                                            onUpdateDraftRow={updateDraftRow}
                                            draftStartTime={draftStartTime}
                                            onDraftStartTimeChange={setDraftStartTime}
                                            draftEndTime={draftEndTime}
                                            onDraftEndTimeChange={setDraftEndTime}
                                        />
                                        <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1, borderTop: '1px solid #e0e0e0' }}>
                                            {editing ? (
                                                <>
                                                    <MuiButton onClick={handleCancelEdit} colors="secondary" variant="outlined">
                                                        Cancel
                                                    </MuiButton>
                                                    <MuiButton onClick={handleSaveEdit} disabled={isSaving}>
                                                        {isSaving ? 'Saving...' : 'Submit'}
                                                    </MuiButton>
                                                </>
                                            ) : (
                                                <>
                                                    <MuiButton onClick={() => handleStartEdit(event)} disabled={!eventCanEdit}>
                                                        Edit
                                                    </MuiButton>
                                                    {isCancellableEvent(event) && (
                                                        <MuiButton
                                                            onClick={() => handleOpenCancel(event)}
                                                            colors="secondary"
                                                            variant="outlined"
                                                        >
                                                            Cancel Request
                                                        </MuiButton>
                                                    )}
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                ) : (
                    <div className="calendar-wrapper">
                        <Calendar
                            localizer={localizer}
                            events={aggregatedEvents}
                            startAccessor="start"
                            endAccessor="end"
                            view={view}
                            onView={handleViewChange}
                            date={currentDate}
                            onNavigate={handleNavigate}
                            components={calendarComponents}
                            eventPropGetter={eventStyleGetter}
                            onSelectEvent={handleSelectEvent}
                            views={['month', 'week', 'day']}
                            popup={true}
                            showAllEvents={true}
                        />
                    </div>
                )}
            </Box>

            {/* Status Legend */}
            <Box sx={{
                py: 1,
                px: 1.5,
                backgroundColor: '#f9f9f9',
                borderRadius: '6px',
                border: '1px solid #e0e0e0',
            }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', fontSize: '0.8125rem', mr: 1 }}>
                        Status:
                    </Typography>
                    {statusLegend.map((item) => (
                        <Box key={item.status} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box sx={{
                                width: '14px',
                                height: '14px',
                                backgroundColor: getStatusColor(item.status),
                                borderRadius: '3px',
                                flexShrink: 0
                            }} />
                            <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: '#666' }}>
                                {item.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Click-to-open dialog: used for month/week views only. In day
                view, the same content renders inline above (no dialog). */}
            <Dialog open={isModalOpen && view !== 'day'} onClose={handleCloseModal} maxWidth="md" fullWidth>
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #e0e0e0',
                    pb: 2
                }}>
                    {selectedEvent?.resource.shiftType === 'GIG' ? (<Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Shift Details - {selectedEvent && moment(selectedEvent.start).format('MMMM D, YYYY')} - {selectedEvent?.resource.events.reduce(
                            (sum, e) => sum + (typeof e.requestHours === 'number' ? e.requestHours : 0),
                            0
                        )} Hours
                    </Typography>
                    ) : (
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Shift Details - {selectedEvent && moment(selectedEvent.start).format('MMMM D, YYYY')}
                        </Typography>
                    )}
                    <IconButton onClick={handleCloseModal} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {selectedEvent && (
                        <ShiftDetailCard
                            event={selectedEvent}
                            skillLabelByCd={skillLabelByCd}
                            isEditing={isEditing}
                            draftNotes={draftNotes}
                            onDraftNotesChange={setDraftNotes}
                            draftRows={draftRows}
                            onUpdateDraftRow={updateDraftRow}
                            draftStartTime={draftStartTime}
                            onDraftStartTimeChange={setDraftStartTime}
                            draftEndTime={draftEndTime}
                            onDraftEndTimeChange={setDraftEndTime}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', justifyContent: 'space-between' }}>
                    {isEditing ? (
                        <>
                            <MuiButton onClick={handleCancelEdit} colors="secondary" variant="outlined">
                                Cancel
                            </MuiButton>
                            <MuiButton onClick={handleSaveEdit} disabled={isSaving}>
                                {isSaving ? 'Saving...' : 'Submit'}
                            </MuiButton>
                        </>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <MuiButton onClick={() => handleStartEdit()} disabled={!canEdit}>
                                    Edit
                                </MuiButton>
                                {selectedEvent && isCancellableEvent(selectedEvent) && (
                                    <MuiButton
                                        onClick={() => handleOpenCancel(selectedEvent)}
                                        colors="secondary"
                                        variant="outlined"
                                    >
                                        Cancel Request
                                    </MuiButton>
                                )}
                            </Box>
                            <MuiButton onClick={handleCloseModal} colors="secondary" variant="outlined">
                                Close
                            </MuiButton>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Cancel confirmation dialog: confirms cancelling this day's gig
                shift request. */}
            <Dialog open={!!cancelTarget} onClose={handleCloseCancel} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', pb: 2 }}>
                    Cancel Gig Shift Request
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {cancelTarget && (
                        <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                <Box>
                                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>Date</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {moment(cancelTarget.start).format('MMMM D, YYYY')}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>Shift</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {shiftOptions.find((o) => o.value === cancelTarget.resource.events[0]?.team)?.label
                                            || cancelTarget.resource.events[0]?.team || '—'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>Skill</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {(() => {
                                            const raw = cancelTarget.resource.skillCd || cancelTarget.resource.skillDesc || cancelTarget.resource.skill;
                                            if (!raw) return '—';
                                            return skillLabelByCd[raw] || raw;
                                        })()}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>Hours</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {cancelTarget.resource.events.reduce(
                                            (sum, e) => sum + (typeof e.requestHours === 'number' ? e.requestHours : 0),
                                            0
                                        ) || '—'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Stack spacing={1} sx={{ width: '100%' }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Are you sure you want to cancel this gig shift request? This action cannot be undone.
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <MuiButton onClick={handleCloseCancel} colors="secondary" variant="outlined" disabled={isCancelling}>
                                Back
                            </MuiButton>
                            <MuiButton onClick={handleConfirmCancel} disabled={isCancelling}>
                                {isCancelling ? 'Cancelling...' : 'Confirm Cancel'}
                            </MuiButton>
                        </Box>
                    </Stack>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GigWorkerShiftsCalendar;
